import { FormControlLabel } from '@mui/material';
import { useFormState } from 'react-final-form';

import { Divider, Typography, Box, Stack } from '@cast/design-system';

import { PreviewYaml } from 'components/code';
import {
  ControlWithDescription,
  InlineWithDescription,
} from 'components/form-layouts';
import { CustomLabelsStack } from 'features/cluster/autoscaler/hooks/useNodeTemplateDrawer/CustomLabelsStack';
import { CustomTaintsStack } from 'features/cluster/autoscaler/hooks/useNodeTemplateDrawer/CustomTaintsStack';

import { MAX_GPU_TIME_SLICING_REPLICAS } from '../constants';
import { getGpuTimeSlicingAvailability, getMigAvailability } from '../_utils';

import { ProvisionNodesCustomConfigurationFormValues } from '../types';

import { RffCheckbox, RffInput } from '@cast/shared-rff-ui';
import { EnhancedEdgeLocation } from '../../../types';

type Props = {
  edgeLocation: EnhancedEdgeLocation;
};

export const ProvisionNodesCustomConfigurationSlideForm = ({
  edgeLocation,
}: Props) => {
  const { values } = useFormState<ProvisionNodesCustomConfigurationFormValues>({
    subscription: { values: true },
  });

  const { shouldTaint, shouldUseCustomLabels, _customTaints, _customLabels } =
    values;

  const migAvailability = getMigAvailability({ edgeLocation });
  const timeSlicingAvailability = getGpuTimeSlicingAvailability({
    edgeLocation,
  });

  const customTaints = _customTaints?.filter((t) => t?.key) ?? [];
  const customLabels = _customLabels?.filter((l) => l?.key) ?? [];

  const yamlContent = {
    ...(shouldUseCustomLabels
      ? {
          nodeSelector: customLabels.length
            ? Object.assign(
                {},
                ...customLabels.map(({ key, value }) => ({
                  [key]: `"${value ?? ''}"`,
                }))
              )
            : '',
        }
      : {}),
    ...(shouldTaint
      ? {
          tolerations: customTaints.map(({ key, value, effect }) => ({
            key: `"${key}"`,
            value: `"${value ?? ''}"`,
            effect: `"${effect}"`,
          })),
        }
      : {}),
  };

  return (
    <Stack>
      <InlineWithDescription
        borderBottom="none"
        description={
          <ControlWithDescription label="Kubernetes orchestration">
            Control how workloads are scheduled onto this node.
          </ControlWithDescription>
        }
        controls={
          <Stack gap="24px">
            <Stack gap="14px">
              <FormControlLabel
                sx={{ gap: '8px', ml: 0 }}
                control={
                  <RffCheckbox
                    sx={{ alignSelf: 'flex-start', marginTop: '2px' }}
                    size="small"
                    name="shouldTaint"
                    testId="should-taint-toggle"
                  />
                }
                label={
                  <Stack pt={2} gap={2}>
                    <Typography variant="P12M">Taint nodes</Typography>
                    <Typography variant="P12R" color="textSecondary">
                      Pods without matching tolerations will not be scheduled on
                      nodes provisioned in this edge location.
                    </Typography>
                  </Stack>
                }
              />
              {shouldTaint && <CustomTaintsStack disabled={false} />}
            </Stack>

            <Stack gap="14px">
              <FormControlLabel
                sx={{ gap: '8px', ml: 0 }}
                control={
                  <RffCheckbox
                    sx={{ alignSelf: 'flex-start', marginTop: '2px' }}
                    size="small"
                    name="shouldUseCustomLabels"
                    testId="use-custom-labels-toggle"
                  />
                }
                label={
                  <Stack pt={2} gap={2}>
                    <Typography variant="P12M">Use custom labels</Typography>
                    <Typography variant="P12R" color="textSecondary">
                      Apply custom labels to nodes provisioned in this edge
                      location.
                    </Typography>
                  </Stack>
                }
              />
              {shouldUseCustomLabels && <CustomLabelsStack disabled={false} />}
            </Stack>

            {(shouldTaint || shouldUseCustomLabels) && (
              <Box position="relative" maxWidth={392}>
                <PreviewYaml
                  data={yamlContent}
                  size="md"
                  containerSx={{ padding: '10px 12px' }}
                />
              </Box>
            )}
          </Stack>
        }
      />
      <Divider />
      <InlineWithDescription
        borderBottom="none"
        description={
          <ControlWithDescription label="GPU time-slicing">
            Time-slice a single GPU into multiple virtual replicas for shared
            access.
          </ControlWithDescription>
        }
        controls={
          <RffInput
            name="replicas"
            type="number"
            min={1}
            max={MAX_GPU_TIME_SLICING_REPLICAS}
            label="Replicas"
            placeholder="e.g. 4"
            disabled={!timeSlicingAvailability.available}
            hint={
              !timeSlicingAvailability.available
                ? timeSlicingAvailability.reason
                : undefined
            }
            testId="replicas-input"
          />
        }
      />
      <Divider />
      <InlineWithDescription
        borderBottom="none"
        description={
          <Stack gap={4}>
            <ControlWithDescription label="Multi-instance GPU (MIG)">
              Partition the GPU into isolated slices with dedicated memory. For
              A100/H100 instances.
            </ControlWithDescription>
          </Stack>
        }
        controls={
          <RffInput
            name="partitionSize"
            label="Partition size"
            placeholder="e.g. 1g.5gb"
            disabled={!migAvailability.available}
            hint={
              !migAvailability.available ? migAvailability.reason : undefined
            }
            testId="partition-size-input"
          />
        }
      />
    </Stack>
  );
};
